import React, { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { transcribeAudio } from "../../lib/api";

// Records mic audio, transcribes via Whisper, and returns text via onText(text).
export const VoiceRecorder = ({ token, onText, disabled, label = "Dictate" }) => {
  const [state, setState] = useState("idle"); // idle | recording | processing
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    if (disabled) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1200) {
          setState("idle");
          toast.error("Recording was too short.");
          return;
        }
        setState("processing");
        try {
          const res = await transcribeAudio(token, blob);
          if (res.text) {
            onText?.(res.text);
            toast.success("Transcribed");
          } else {
            toast.info("No speech detected.");
          }
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Could not transcribe.");
        } finally {
          setState("idle");
        }
      };
      mediaRef.current = mr;
      mr.start();
      setState("recording");
    } catch (e) {
      toast.error("Microphone access was blocked.");
    }
  };

  const stop = () => {
    if (mediaRef.current && state === "recording") mediaRef.current.stop();
  };

  const recording = state === "recording";
  const processing = state === "processing";

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={disabled || processing}
      className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg transition-colors ${
        recording
          ? "bg-red-500 text-white hover:bg-red-600"
          : "bg-navy/5 text-navy hover:bg-navy/10"
      } disabled:opacity-50`}
      data-testid="voice-record-button"
      title={recording ? "Stop recording" : "Record a voice note"}
    >
      {processing ? (
        <><Loader2 className="h-4 w-4 animate-spin" /> Transcribing…</>
      ) : recording ? (
        <><Square className="h-4 w-4 fill-current" /> Stop</>
      ) : (
        <><Mic className="h-4 w-4" /> {label}</>
      )}
    </button>
  );
};
